import React, { useEffect } from 'react';
import { DrawData } from '../../types/DrawData';
import SimpleEntriesStep from './SimpleEntriesStep';
import UploadEntriesStep from './UploadEntriesStep';
import useUploadStore from './useUploadStore';

interface EntryConfigurationStepProps {
  drawData: DrawData;
  updateDrawData: (updates: Partial<DrawData>) => void;
  onNext: () => void;
  onBack: () => void;
  readonly?: boolean;
  isLocked?: boolean;
}

const EntryConfigurationStep: React.FC<EntryConfigurationStepProps> = ({
  drawData,
  updateDrawData,
  onNext,
  onBack,
  isLocked = false,
}) => {
  const { commitToDraw, entriesUpload, status, reset } = useUploadStore();

  // Reset upload store when switching to simple entry method
  useEffect(() => {
    if (drawData.entryMethod === 'simple' && (status === 'success' || entriesUpload?.storedFileName)) {
      // Clear upload store when switching from standard/advanced to simple
      // since uploaded file data is not relevant for simple entry method
      reset();
      // Also clear uploaded entries from DrawData if present
      if (drawData.uploadedEntries) {
        updateDrawData({ uploadedEntries: undefined });
      }
    }
  }, [drawData.entryMethod, status, entriesUpload?.storedFileName, reset, updateDrawData, drawData.uploadedEntries]);

  // Handle simple entry method
  if (drawData.entryMethod === 'simple') {
    return (
      <SimpleEntriesStep
        initialCount={drawData.entryCount || 0}
        isDisabled={isLocked}
        onContinue={(count) => {
          if (!isLocked) {
            updateDrawData({ entryCount: count });
          }
          onNext();
        }}
        onBack={onBack}
      />
    );
  }

  if (drawData.entryMethod === 'standard' || drawData.entryMethod === 'advanced') {
    return (
      <UploadEntriesStep
        entryMethod={drawData.entryMethod}
        drawId={drawData.drawId}
        isLocked={isLocked}
        onNext={() => {
          if (!isLocked) {
            const { uploadedEntries, csvConfirmationSettings, calculatedEntryCount } = commitToDraw();
            updateDrawData({
              uploadedEntries,
              csvConfirmationSettings,
              entryCount: calculatedEntryCount || 0
            });
          }
          onNext();
        }}
        onBack={onBack}
      />
    );
  }

  // Fallback
  return <></>;
};

export default EntryConfigurationStep;
